import Chart from 'chart.js/auto';
import { chartColors } from './chartColors';
import { downloadChartCSV, formatTooltipLabel } from './chartHelpers';

class TrendChartComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['survey_id', 'question_id', 'trend_years'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'trend_years') {
            const trendYears = JSON.parse(newValue);
            this.trendYearsArray = [];

            Object.entries(trendYears).forEach(([key, value]) => {
                this.trendYearsArray.push({ [key]: value });
            });
        } else {
            this[name.replace('-', '_')] = newValue;
        }
    }

    parseTrendYears(trendYearsString) {
        const trendYears = {};
        trendYearsString.split('|').forEach(pair => {
            const [key, value] = pair.split(':');
            trendYears[key] = value;
        });
        return trendYears;
    }

    connectedCallback() {
        this.render();

        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const data = this.fetchData();
                        this.buildChart(data);
                        this.observer.disconnect();
                    }
                });
            },
            { root: null, threshold: 0.1 },
        );

        this.observer.observe(this);
    }

    async fetchData() {
        const surveyId = this.getAttribute('survey_id');
        const questionId = this.getAttribute('question_id');
        const answerType = this.getAttribute('answer_type');

        const firstKey = Object.keys(this.trendYearsArray[0])[0];
        const trendYearsKey = this.trendYearsArray[0][firstKey];

        const queryParams = new URLSearchParams({
            survey_id: surveyId,
            question_id: questionId,
            group_ids: trendYearsKey,
        });

        const url = `${benchmarktool.api_url}/api/answers?${queryParams.toString()}`;

        try {
            const response = await fetch(url);
            const data = await response.json();
            this.renderChart(data, answerType);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    initChartDownloadCsv = () => {
        const downloadButton = document.querySelector(
            '[data-download-trend-button-question-id="' + this.questionId + '"]',
        );

        if (!downloadButton) {
            console.error('No download link button found');
            return;
        }

        downloadButton.addEventListener('click', event => {
            event.preventDefault();

            const chartTitle = downloadButton.getAttribute('data-trend-chart-title') || 'Chart';
            downloadChartCSV({
                chart: this.chart,
                shadowRoot: this.shadowRoot,
                filename: chartTitle,
            });
        });
    };

    buildChart(data) {
        const questionId = this.getAttribute('question_id');
        this.questionId = questionId;
    }

    renderChart(data, answerType) {
        const labels = Object.keys(data);
        const answerData = {};

        labels.forEach(year => {
            data[year].forEach(entry => {
                const { answer_label, answer_value } = entry;

                if (!answerData[answer_label]) {
                    answerData[answer_label] = [];
                }

                const processedValue = answerType === 'Cijfer' ? Math.round(answer_value) : answer_value;
                answerData[answer_label].push(processedValue);
            });
        });

        const datasets = Object.keys(answerData).map((label, index) => ({
            label: label,
            data: answerData[label],
            fill: false,
            backgroundColor: chartColors[index],
        }));

        this.shadowRoot.querySelector('#trend-chart-container').innerHTML = '';
        const canvas = document.createElement('canvas');

        const borderWidthValue = 2.5;
        const hoverOffsetValue = 1;

        const originalChartOptions = {
            indexAxis: 'x',
            tension: 0.2,
            pointRadius: 4,
            aspectRatio: 2,
            responsive: true,
            maintainAspectRatio: false,
            borderWidth: borderWidthValue,
            hoverOffset: hoverOffsetValue,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#595959',
                        font: {
                            family: 'PP Object Sans',
                            size: 11,
                            weight: 'normal',
                            lineHeight: 2,
                        },
                        usePointStyle: true,
                        pointStyle: 'rect',
                    },
                },
                tooltip: {
                    padding: 12,
                    borderWidth: 1,
                    cornerRadius: 8,
                    borderColor: '#e6edff',
                    backgroundColor: '#fff',
                    titleColor: '#333',
                    titleFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'bold',
                    },
                    bodyColor: '#595959',
                    bodyFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'normal',
                    },
                    usePointStyle: true,
                    pointStyle: 'rect',
                    callbacks: {
                        label: function (context) {
                            return formatTooltipLabel(context, answerType);
                        },
                    },
                },
            },
        };

        const maxXAxis = 100;
        const newChartOptions = {
            scales: {
                x: {
                    ticks: {
                        color: '#333',
                        font: {
                            family: 'PP Object Sans',
                            size: 10,
                            weight: 'bold',
                            lineHeight: 2,
                        },
                    },
                    max: maxXAxis,
                },
                y: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        callback: value => {
                            if (answerType === 'Percentage') {
                                return `${value}%`;
                            }

                            if (answerType === 'Cijfer') {
                                return Math.round(value);
                            }

                            return value;
                        },
                        color: '#595959',
                        font: {
                            family: 'PP Object Sans',
                            size: 10,
                            weight: 'normal',
                            lineHeight: 2,
                        },
                        stepSize: answerType === 'Cijfer' ? 1 : undefined,
                    },
                    min: answerType === 'Cijfer' ? 0 : undefined,
                    max: answerType === 'Cijfer' ? 10 : undefined,
                },
            },
        };

        const finalChartOptions = {
            ...originalChartOptions,
            ...newChartOptions,
        };

        this.shadowRoot.querySelector('#trend-chart-container').appendChild(canvas);
        this.chart = new Chart(canvas.getContext('2d'), {
            type: 'line',
            data: {
                labels: labels,
                datasets: datasets,
            },
            options: finalChartOptions,
        });

        this.initChartDownloadCsv();
    }

    render() {
        this.shadowRoot.innerHTML = `
             <style>
                .exp-card-chart__body {
                    position: relative;
                    margin-top: 16px;
                    margin-bottom: 24px;
                    height: 500px;
                }
            </style>

            <div id="trend-chart-container" class="exp-card-chart__body">Aan het laden...</div>
        `;
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

customElements.define('trend-chart-component', TrendChartComponent);
